import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as z from 'zod';
import axios from 'axios';
import { 
  MapPin, Navigation, Building2, User, Phone, Mail, 
  Map, Globe, FileText, IdCard, CheckCircle, 
  AlertCircle, Loader2, Home, Briefcase, Landmark, Lock
} from 'lucide-react';

// ────────── VALIDATION SCHEMA ──────────
const traderSchema = z.object({
  tin: z.string().min(5, "TIN must be at least 5 characters").max(20, "TIN too long"),
  phone: z.string().regex(/^(\+?[0-9]{10,15})$/, "Invalid phone format (e.g., 0912345678)"),
  email: z.string().optional(),
  city: z.string().min(2, "City must be at least 2 characters"),
  business_name: z.string().min(3, "Business Name must be at least 3 characters"),
  subcity: z.string().min(2, "Subcity is required"),
  name: z.string().min(2, "Full name is required"),
  woreda: z.string().min(1, "Woreda is required"),
  region: z.string().min(2, "Region is required"), // Changed from optional to required
  address: z.string().min(5, "Please provide a detailed address"),
  latitude: z.preprocess((val) => (val === '' || val === undefined ? undefined : Number(val)), z.number().optional()),
  longitude: z.preprocess((val) => (val === '' || val === undefined ? undefined : Number(val)), z.number().optional()),
  license_photo: z.any().optional(),
  national_id_photo: z.any().optional(),
  status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
});

// ────────── API CONFIG ──────────
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  headers: { Accept: 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ────────── ETHIOPIAN REGIONS ──────────
const ethiopianRegions = [
  { value: 'addis_ababa', label: 'Addis Ababa' },
  { value: 'afar', label: 'Afar Region' },
  { value: 'amhara', label: 'Amhara Region' },
  { value: 'benishangul_gumuz', label: 'Benishangul-Gumuz Region' },
  { value: 'dire_dawa', label: 'Dire Dawa' },
  { value: 'gambela', label: 'Gambela Region' },
  { value: 'harari', label: 'Harari Region' },
  { value: 'oromia', label: 'Oromia Region' },
  { value: 'sidama', label: 'Sidama Region' },
  { value: 'somalia', label: 'Somali Region' },
  { value: 'south_west_ethiopia', label: 'South West Ethiopia Peoples\' Region' },
  { value: 'southern_nations', label: 'Southern Nations, Nationalities, and Peoples\' Region' },
  { value: 'tigray', label: 'Tigray Region' }
];

// ────────── COMPONENT ──────────
export default function AddTrader({ onClose, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);
  const [role, setRole] = useState("");
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(traderSchema),
    defaultValues: { status: 'pending', region: '' },
  });
  
  const latitude = watch('latitude');
  const longitude = watch('longitude');

  // ────────── CHECK AUTHENTICATION ──────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
    }
  }, [navigate]);

  // ────────── FETCH USER ROLE ──────────
  useEffect(() => {
    const fetchRole = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await api.get('/api/role');
        setRole(response.data);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };
    fetchRole();
  }, []);

  // ────────── AUTOMATIC GEOLOCATION ON PAGE LOAD ──────────
  useEffect(() => {
    const getLocationOnLoad = () => {
      if (!navigator.geolocation) {
        console.warn('Geolocation is not supported');
        return;
      }

      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setValue('latitude', position.coords.latitude);
          setValue('longitude', position.coords.longitude);
          setLocationDetected(true);
          setIsLocating(false);
          
          Swal.fire({
            icon: 'success',
            title: 'Location Detected!',
            html: `<div class="text-left">
                     <p class="mb-2"><strong>Latitude:</strong> ${position.coords.latitude.toFixed(6)}</p>
                     <p><strong>Longitude:</strong> ${position.coords.longitude.toFixed(6)}</p>
                   </div>`,
            timer: 3000,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
            customClass: {
              popup: 'rounded-xl',
            },
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsLocating(false);
          
          let errorMessage = 'Unable to retrieve your location.';
          if (error.code === 1) errorMessage = 'Location access denied. Please enable GPS and refresh.';
          if (error.code === 2) errorMessage = 'Location unavailable. Please check your GPS.';
          if (error.code === 3) errorMessage = 'Location request timed out.';
          
          Swal.fire({
            icon: 'warning',
            title: 'Location Access',
            text: errorMessage,
            confirmButtonText: 'OK',
            customClass: {
              popup: 'rounded-xl',
            },
          });
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000,
          maximumAge: 0 
        }
      );
    };

    const timer = setTimeout(() => {
      getLocationOnLoad();
    }, 500);

    return () => clearTimeout(timer);
  }, [setValue]);

  // ────────── MANUAL GEOLOCATION ──────────
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation is not supported by your browser', 'error');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setValue('latitude', position.coords.latitude);
        setValue('longitude', position.coords.longitude);
        setLocationDetected(true);
        setIsLocating(false);
        Swal.fire({
          icon: 'success',
          title: 'Location Updated!',
          html: `<div class="text-center">
                   <p class="text-sm">📍 Latitude: ${position.coords.latitude.toFixed(6)}</p>
                   <p class="text-sm">📍 Longitude: ${position.coords.longitude.toFixed(6)}</p>
                 </div>`,
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true,
        });
      },
      (error) => {
        setIsLocating(false);
        let errorMessage = 'Unable to retrieve your location.';
        if (error.code === 1) errorMessage = 'Please enable location access in your browser settings.';
        
        Swal.fire({
          icon: 'error',
          title: 'Location Failed',
          text: errorMessage,
          confirmButtonText: 'Try Again',
          customClass: {
            popup: 'rounded-xl',
          },
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ────────── SUBMIT HANDLER ──────────
  const onSubmit = async (data) => {
    // Validate location is captured
    if (!data.latitude || !data.longitude) {
      Swal.fire({
        icon: 'warning',
        title: 'Location Required',
        text: 'Please enable location access to register a trader.',
        confirmButtonText: 'Get Location',
        customClass: {
          popup: 'rounded-xl',
        },
      }).then((result) => {
        if (result.isConfirmed) handleGetLocation();
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === 'license_photo' || key === 'national_id_photo') {
          if (data[key]?.[0]) formData.append(key, data[key][0]);
        } else if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });

      await api.post('/api/addtrader', formData);
      
      Swal.fire({
        icon: 'success',
        title: 'Registration Successful!',
        html: `<p class="text-green-600">Trader has been registered successfully.</p>
               <p class="text-sm text-gray-500 mt-2">A confirmation email has been sent.</p>`,
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
        },
      });
      
      reset();
      setLocationDetected(false);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
      
    } catch (error) {
      const message = error.response?.data?.message || 'Submission failed. Please try again.';
      Swal.fire({
        icon: 'error',
        title: 'Registration Failed',
        text: message,
        confirmButtonText: 'Try Again',
        customClass: {
          popup: 'rounded-xl',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Input field component for consistency
  const InputField = ({ icon: Icon, label, name, placeholder, type = "text", required = true, disabled = false, readOnly = false, ...props }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className={`h-5 w-5 transition-colors ${disabled || readOnly ? 'text-gray-400' : 'text-gray-400 group-focus-within:text-indigo-500'}`} />
        </div>
        <input
          type={type}
          {...register(name)}
          disabled={disabled}
          readOnly={readOnly}
          className={`block w-full pl-10 pr-3 py-2.5 border ${errors[name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} 
                     rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent
                     transition-all duration-200 ${disabled || readOnly ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-gray-50 hover:bg-white'}
                     ${readOnly && 'cursor-default'}`}
          placeholder={placeholder}
          {...props}
        />
        {(disabled || readOnly) && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Lock className="h-4 w-4 text-gray-400" />
          </div>
        )}
      </div>
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {errors[name].message}
        </p>
      )}
    </div>
  );

  // Select field component for dropdown
  const SelectField = ({ icon: Icon, label, name, options, required = true, placeholder = "Select an option" }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
        </div>
        <select
          {...register(name)}
          className={`block w-full pl-10 pr-3 py-2.5 border ${errors[name] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'} 
                     rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent
                     transition-all duration-200 bg-gray-50 hover:bg-white appearance-none`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {errors[name].message}
        </p>
      )}
    </div>
  );

  const TextAreaField = ({ icon: Icon, label, name, placeholder, rows = 3, required = true }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative group">
        <div className="absolute top-3 left-0 pl-3 pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
        </div>
        <textarea
          {...register(name)}
          rows={rows}
          className={`block w-full pl-10 pr-3 py-2 border ${errors[name] ? 'border-red-300' : 'border-gray-300'} 
                     rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 
                     focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white`}
          placeholder={placeholder}
        />
      </div>
      {errors[name] && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <AlertCircle size={12} />
          {errors[name].message}
        </p>
      )}
    </div>
  );

  const FileField = ({ icon: Icon, label, name, accept = "image/*" }) => (
    <div className="space-y-1.5">
      <label className="block text-sm font-semibold text-gray-700">{label}</label>
      <div className="relative">
        <input
          type="file"
          {...register(name)}
          accept={accept}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 
                     file:rounded-lg file:border-0 file:text-sm file:font-semibold 
                     file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 
                     border border-gray-300 rounded-lg cursor-pointer focus:outline-none"
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Register New Trader</h1>
                <p className="text-indigo-100">Fill in the trader's information below</p>
              </div>
              <div className="hidden md:block">
                <Briefcase className="h-16 w-16 text-white opacity-20" />
              </div>
            </div>
          </div>
          
          {/* Location Status Banner */}
          <div className={`px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b ${locationDetected ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-3">
              {isLocating ? (
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              ) : locationDetected ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              )}
              <span className={`text-sm font-medium ${locationDetected ? 'text-green-700' : 'text-yellow-700'}`}>
                {isLocating 
                  ? 'Detecting your location...' 
                  : locationDetected 
                    ? `✓ Location locked and verified (${parseFloat(latitude).toFixed(6)}, ${parseFloat(longitude).toFixed(6)})` 
                    : '⚠ Location not detected. Click the button below to enable.'}
              </span>
            </div>
            <button
              type="button"
              onClick={handleGetLocation}
              disabled={isLocating}
              className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-lg 
                       hover:bg-indigo-700 transition-all disabled:opacity-50 text-sm font-medium
                       shadow-sm"
            >
              <Navigation size={16} />
              Update Location
            </button>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8">
            {/* Form Sections */}
            <div className="space-y-8">
              {/* Business Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-800">Business Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <InputField icon={Landmark} label="TIN Number" name="tin" placeholder="Enter TIN number (e.g., ET0123456789)" />
                  <InputField icon={Briefcase} label="Business Name" name="business_name" placeholder="Enter registered business name" />
                </div>
              </div>

              {/* Personal Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <User className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-800">Personal Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <InputField icon={User} label="Full Name" name="name" placeholder="Enter full name" />
                  <InputField icon={Phone} label="Phone Number" name="phone" placeholder="e.g., 0912345678" />
                  <InputField icon={Mail} label="Email Address" name="email" type="email" placeholder="trader@example.com" />
                </div>
              </div>

              {/* Address Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <Map className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-800">Address Information</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <SelectField 
                    icon={Globe} 
                    label="Region" 
                    name="region" 
                    options={ethiopianRegions}
                    placeholder="Select region"
                    required={true}
                  />
                  <InputField icon={Globe} label="City" name="city" placeholder="Enter city name" />
                  <InputField icon={MapPin} label="Subcity" name="subcity" placeholder="Enter subcity" />
                  <InputField icon={MapPin} label="Woreda" name="woreda" placeholder="Enter woreda number" />
                  <div className="grid grid-cols-2 gap-3">
                    <InputField 
                      icon={MapPin} 
                      label="Latitude" 
                      name="latitude" 
                      type="number" 
                      step="any" 
                      placeholder="Auto-detected"
                      readOnly={true}
                      disabled={true}
                    />
                    <InputField 
                      icon={MapPin} 
                      label="Longitude" 
                      name="longitude" 
                      type="number" 
                      step="any" 
                      placeholder="Auto-detected"
                      readOnly={true}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <TextAreaField icon={Home} label="Full Address" name="address" placeholder="Enter complete address (street, building, etc.)" rows={2} />
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700 flex items-center gap-2">
                    <Lock size={14} />
                    Location coordinates are automatically detected and cannot be edited manually to ensure accuracy.
                  </p>
                </div>
              </div>

              {/* Documents Section */}
              <div>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-gray-800">Supporting Documents</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FileField icon={FileText} label="Business License" name="license_photo" />
                  <FileField icon={IdCard} label="National ID" name="national_id_photo" />
                </div>
                <p className="text-xs text-gray-500 mt-3">* Upload clear images (JPG, PNG format)</p>
              </div>

              {/* Hidden Status Field */}
              <input type="hidden" {...register('status')} />

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                           py-3.5 px-6 rounded-xl font-bold text-lg hover:from-indigo-700 
                           hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed 
                           transition-all duration-200 transform hover:scale-[1.02] 
                           shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Registering Trader...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Register Trader
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}